import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Locate, Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LocationDialog({ location, onSave }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    setBusy(true);
    try {
      const r = await api.get(`/geocode?q=${encodeURIComponent(query)}`);
      setResults(r.data.results || []);
      if (!(r.data.results || []).length) toast.message("No matches found.");
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setBusy(false);
    }
  };

  const useGeo = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation unavailable in this browser.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        await onSave({ label: "Current location", latitude, longitude });
        setBusy(false);
        setOpen(false);
        toast.success("Location set from device.");
      },
      (err) => {
        setBusy(false);
        toast.error(`Permission denied: ${err.message}`);
      },
      { timeout: 12000 }
    );
  };

  const choose = async (r) => {
    await onSave({ label: r.label, latitude: r.latitude, longitude: r.longitude });
    toast.success(`Tending the garden at ${r.label}.`);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-border/80"
          data-testid="set-location-btn"
        >
          <MapPin className="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
          <span className="hidden sm:inline text-xs">
            {location?.label ? location.label : "Set location"}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Where does your garden grow?</DialogTitle>
          <DialogDescription>
            The Sage uses your local skies to read the rain, sun and seasons.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Button
            type="button"
            onClick={useGeo}
            disabled={busy}
            className="w-full gap-2 bg-primary hover:bg-primary/90"
            data-testid="use-my-location-btn"
          >
            <Locate className="w-4 h-4" strokeWidth={1.5} />
            Use my current location
          </Button>

          <div className="sage-divider" />

          <form onSubmit={doSearch} className="space-y-2">
            <Label className="label-overline">Search city or zip</Label>
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Portland, OR"
                data-testid="location-search-input"
              />
              <Button type="submit" disabled={busy} variant="secondary" data-testid="location-search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </form>

          {results.length > 0 && (
            <ul className="border border-border rounded-md divide-y divide-border max-h-56 overflow-auto">
              {results.map((r, i) => (
                <li key={`${r.latitude}-${r.longitude}-${i}`}>
                  <button
                    type="button"
                    onClick={() => choose(r)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/60 text-sm"
                    data-testid={`location-result-${i}`}
                  >
                    {r.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="text-xs label-overline">
          {location?.label ? `current — ${location.label}` : "no location set"}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
